import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Checkbox,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Visibility, Edit, Delete, Search } from "@mui/icons-material";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";


const OddsList = () => {
  const [odds, setOdds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const pageSize = 10;

  // ============================
  // FETCH BANNERS
  // ============================
  const fetchOdds = async (page = 1, searchQuery = "") => {
    setLoading(true);
    try {
      const url = `${ENDPOINT.ODDS_LIST}?page=${page}&search=${encodeURIComponent(
        searchQuery
      )}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;

      // *** IMPORTANT: YOUR RESPONSE USES "results" ***
      setOdds(data.results || []);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
      setCurrentPage(page);
    } catch (err) {
      toast.error("Failed to load odds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(!token){
      navigate("/login")
    }else{
      fetchOdds(1, "");
    }
  }, []);

  // ============================
  // SEARCH
  // ============================
  const handleSearch = (e) => {
    e.preventDefault();
    fetchOdds(1, search);
  };

  // ============================
  // PAGINATION
  // ============================
  const handleNext = () => {
    if (currentPage < totalPages) {
      fetchOdds(currentPage + 1, search);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      fetchOdds(currentPage - 1, search);
    }
  };

  // ============================
  // DELETE
  // ============================
  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const closeDeleteDialog = () => setOpenDialog(false);

  const handleDelete = async () => {
    try {
      await axios.delete(ENDPOINT.ODDS_DELETE(deleteId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Odd deleted!");

      if (odds.length === 1 && currentPage > 1) {
        fetchOdds(currentPage - 1, search);
      } else {
        fetchOdds(currentPage, search);
      }
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      closeDeleteDialog();
    }
  };

  // ============================
  // UI
  // ============================
  return (
    <Container maxWidth="xl" sx={{ mt: 5 }}>
      <ToastContainer />

      {/* SEARCH */}
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            size="small"
            placeholder="Search Odd..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{ ml: 1 }}
            disabled={loading}
          >
            Search
          </Button>
        </Box>

        <Button
          variant="contained"
          color="success"
          onClick={() => navigate("/odds/create")}
        >
          + Create Odd
        </Button>
      </Stack>

      {/* TABLE */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>League</TableCell>
                  <TableCell>Home Team</TableCell>
                  <TableCell>Away Team</TableCell>
                  <TableCell>Body Odds</TableCell>
                  <TableCell>Over Under Odds</TableCell>
                  <TableCell>Home Score</TableCell>
                  <TableCell>Away Score</TableCell>
                  <TableCell>Is Finished</TableCell>
                  <TableCell>Match Time</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {odds.length ? (
                  odds.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {(currentPage - 1) * pageSize + index + 1}
                      </TableCell>


                      <TableCell>{item.league.name}</TableCell>

                      <TableCell>
                        <Avatar
                          src={`${IMAGE_BASE_URL}${item.home_team.image}`}
                          variant="rounded"
                          sx={{ width: 50, height: 50 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Avatar
                          src={`${IMAGE_BASE_URL}${item.away_team.image}`}
                          variant="rounded"
                          sx={{ width: 50, height: 50 }}
                        />
                      </TableCell>

                      <TableCell>{item.body_odds}</TableCell>
                      <TableCell>{item.over_under_odds}</TableCell>
                      <TableCell>{item.home_score}</TableCell>
                      <TableCell>{item.away_score}</TableCell>
                      <TableCell>
                        <Checkbox
                            checked={item.is_finished == true}
                          />
                      </TableCell>

                     <TableCell>
                      {dayjs(item.match_time).format("DD MMM YYYY, HH:mm")}
                    </TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >

                          <Tooltip title="Update">
                            <IconButton
                              color="warning"
                              onClick={() => navigate(`/odds/update/${item.id}`)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => openDeleteDialog(item.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No odds found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}
          <Divider />

          <Stack direction="row" justifyContent="space-between" p={2}>
            <Typography>
              Total: <b>{count}</b> | Pages: <b>{totalPages}</b>
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button disabled={currentPage <= 1} onClick={handlePrevious}>
                ← Previous
              </Button>

              <Typography>
                Page {currentPage} / {totalPages}
              </Typography>

              <Button disabled={currentPage >= totalPages} onClick={handleNext}>
                Next →
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* DELETE DIALOG */}
      <Dialog open={openDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this odd?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OddsList;
